import { fromUnixTime } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { ApiISODateTimeString } from 'api/types/ApiTypedDate';
import { BodyMicro } from 'components/Text/BodyMicro';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import type { SearchAlgoliaInternshipHit } from 'modules/search/algolia/types/SearchAlgoliaInternshipHit';
import { SearchHitImage } from 'modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from 'modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitImageHolder,
} from 'modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from 'modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import { type SearchSavedListingsStore } from 'modules/search/zustand-stores/searchSavedListingsStore';
import { getWorkLocationString } from 'utils/address/getWorkLocationString';
import { distanceInWordsToNow } from 'utils/date';

import { SearchInternshipHitInfo } from './SearchInternshipHitInfo';

type Props = {
  hit: SearchAlgoliaInternshipHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  hideOwnerBanner: boolean;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  actions?: ReactNode;
};

export function SearchInternshipHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  hideOwnerBanner,
  highlighted,
  application,
  actions,
}: Props) {
  const { i18n } = useI18n();
  const { user } = useUser();

  const {
    objectID,
    name,
    published,
    city,
    state,
    country,
    orgID,
    orgName,
    orgType,
    logo,
    locationType,
    remoteZone,
    isPostedAnonymously,
  } = hit;

  const location = useMemo(
    () =>
      getWorkLocationString({
        i18n,
        remoteOptions: {
          remoteZone,
          remoteCountry: country,
          remoteState: state,
        },
        address: {
          city,
          country,
          stateCode: state,
        },
      }),
    [i18n, remoteZone, city, country, state],
  );

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (published) {
      stats.push(
        <BodyMicro lineHeight={1}>
          {i18n('Posted %(postedAt)s', {
            postedAt: distanceInWordsToNow(fromUnixTime(published)),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [i18n, published]);

  const ownerOrg = user ? user.orgs.find((o) => o.id === orgID) : null;

  return (
    <SearchHitHolder data-qa-id="search-result" data-hit-id={objectID}>
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          actions={actions}
          roundedTop={hideOwnerBanner ?? !ownerOrg}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={null}
              source={null}
              name={name}
              logo={logo}
            />
          </SearchHitImageHolder>

          <SearchInternshipHitInfo
            application={application}
            isPostedAnonymously={isPostedAnonymously}
            listingStats={listingStats}
            location={location}
            locationType={locationType}
            name={name}
            orgName={orgName}
            orgType={orgType}
            showLabel={showLabel}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
